import React, { useState, useEffect } from 'react';
import { US_STATES } from '../lib/usStates';
import { useUser } from '../store/useAuthStore';
import { apiClient } from '../lib/api';
import { useNotifications } from '../hooks/useNotifications';
import {
  Building2,
  Plus,
  Edit3,
  Trash2,
  Search,
  CheckCircle,
  XCircle,
  AlertCircle,
  Star,
  MapPin,
  Package,
  TestTube,
  ChevronDown,
  ChevronUp,
  X
} from 'lucide-react';

interface PharmacyConfig {
  _id: string;
  name: string;
  ncpdpId: string;
  stateRules: string[];
  productRules: Array<{ _id: string; drugName?: string; displayName?: string }>;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Product {
  _id: string;
  drugName: string;
  displayName?: string;
  concentration?: string;
  format?: string;
}

interface FormData {
  name: string;
  ncpdpId: string;
  stateRules: string[];
  productRules: string[]; // Product IDs
  isDefault: boolean;
}

interface TestResult {
  resolvedNcpdpId: string;
  matchedBy: 'state' | 'product' | 'default' | 'env';
  inputState: string | null;
  inputProductIds: string[];
}

const PharmacyConfig: React.FC = () => {
  const user = useUser();
  const { notifySuccess, notifyError } = useNotifications();

  // State management
  const [configs, setConfigs] = useState<PharmacyConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState<PharmacyConfig | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState<FormData>({
    name: '',
    ncpdpId: '',
    stateRules: [],
    productRules: [],
    isDefault: false
  });

  // Products for selection
  const [products, setProducts] = useState<Product[]>([]);
  const [_productsLoading, setProductsLoading] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  // Test routing state
  const [showTestPanel, setShowTestPanel] = useState(false);
  const [testState, setTestState] = useState('');
  const [testProductId, setTestProductId] = useState('');
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testLoading, setTestLoading] = useState(false);

  // Delete confirmation
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Load configs and products on mount
  useEffect(() => {
    if (user?.role === 'admin') {
      loadConfigs();
      loadProducts();
    }
  }, [user]);

  const loadProducts = async () => {
    try {
      setProductsLoading(true);
      // Routing rules can reference any product, and this list resolves their
      // names — a truncated fetch left rules past the first 100 rendering blank
      // and unselectable. getAllProducts paginates rather than trusting a
      // `limit` the endpoint silently caps at 100.
      const response = await apiClient.getAllProducts();
      if (response.success && response.data) {
        setProducts((response.data as any).products || []);
      }
    } catch (error: any) {
      console.error('Failed to load products:', error);
    } finally {
      setProductsLoading(false);
    }
  };

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getPharmacyConfigs();
      if (response.success && response.data) {
        setConfigs(response.data);
      } else {
        notifyError('Failed to Load', response.error || 'Failed to load pharmacy configurations');
      }
    } catch (error: any) {
      notifyError('Error', 'Failed to load pharmacy configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setEditingConfig(null);
    setFormData({
      name: '',
      ncpdpId: '',
      stateRules: [],
      productRules: [],
      isDefault: false
    });
    setProductSearch('');
    setShowFormModal(true);
  };

  const handleOpenEditModal = (config: PharmacyConfig) => {
    setEditingConfig(config);
    setFormData({
      name: config.name,
      ncpdpId: config.ncpdpId,
      stateRules: config.stateRules,
      productRules: config.productRules.map(p => p._id),
      isDefault: config.isDefault
    });
    setProductSearch('');
    setShowFormModal(true);
  };

  const handleCloseModal = () => {
    setShowFormModal(false);
    setEditingConfig(null);
    setFormData({
      name: '',
      ncpdpId: '',
      stateRules: [],
      productRules: [],
      isDefault: false
    });
    setProductSearch('');
  };

  const handleSubmit = async () => {
    // Validate
    if (!formData.name.trim()) {
      notifyError('Validation Error', 'Name is required');
      return;
    }
    if (!formData.ncpdpId.trim()) {
      notifyError('Validation Error', 'NCPDP ID is required');
      return;
    }

    try {
      setSubmitting(true);

      if (editingConfig) {
        // Update
        const response = await apiClient.updatePharmacyConfig(editingConfig._id, {
          name: formData.name.trim(),
          ncpdpId: formData.ncpdpId.trim(),
          stateRules: formData.stateRules,
          productRules: formData.productRules,
          isDefault: formData.isDefault
        });

        if (response.success) {
          notifySuccess('Updated', 'Pharmacy configuration updated successfully');
          handleCloseModal();
          loadConfigs();
        } else {
          notifyError('Update Failed', response.error || 'Failed to update configuration');
        }
      } else {
        // Create
        const response = await apiClient.createPharmacyConfig({
          name: formData.name.trim(),
          ncpdpId: formData.ncpdpId.trim(),
          stateRules: formData.stateRules,
          productRules: formData.productRules,
          isDefault: formData.isDefault
        });

        if (response.success) {
          notifySuccess('Created', 'Pharmacy configuration created successfully');
          handleCloseModal();
          loadConfigs();
        } else {
          notifyError('Create Failed', response.error || 'Failed to create configuration');
        }
      }
    } catch (error: any) {
      notifyError('Error', 'An error occurred while saving');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (configId: string) => {
    try {
      const response = await apiClient.deletePharmacyConfig(configId);
      if (response.success) {
        notifySuccess('Deleted', 'Pharmacy configuration deleted');
        setDeleteConfirmId(null);
        loadConfigs();
      } else {
        notifyError('Delete Failed', response.error || 'Failed to delete configuration');
      }
    } catch (error: any) {
      notifyError('Error', 'Failed to delete configuration');
    }
  };

  const handleTestRouting = async () => {
    try {
      setTestLoading(true);
      const response = await apiClient.testPharmacyRouting({
        state: testState || undefined,
        productIds: testProductId || undefined
      });

      if (response.success && response.data) {
        setTestResult(response.data);
      } else {
        notifyError('Test Failed', response.error || 'Failed to test routing');
      }
    } catch (error: any) {
      notifyError('Error', 'Failed to test routing');
    } finally {
      setTestLoading(false);
    }
  };

  const handleAddState = (state: string) => {
    if (!formData.stateRules.includes(state)) {
      setFormData(prev => ({
        ...prev,
        stateRules: [...prev.stateRules, state]
      }));
    }
  };

  const handleRemoveState = (state: string) => {
    setFormData(prev => ({
      ...prev,
      stateRules: prev.stateRules.filter(s => s !== state)
    }));
  };

  const handleAddProduct = (productId: string) => {
    if (!formData.productRules.includes(productId)) {
      setFormData(prev => ({
        ...prev,
        productRules: [...prev.productRules, productId]
      }));
    }
    setProductSearch('');
  };

  const handleRemoveProduct = (productId: string) => {
    setFormData(prev => ({
      ...prev,
      productRules: prev.productRules.filter(id => id !== productId)
    }));
  };

  // Get product by ID for display
  const getProductById = (productId: string) => {
    return products.find(p => p._id === productId);
  };

  // Filter products for search
  const filteredProducts = products.filter(p => {
    if (!productSearch.trim()) return true;
    const search = productSearch.toLowerCase();
    return (
      p.drugName?.toLowerCase().includes(search) ||
      p.displayName?.toLowerCase().includes(search)
    );
  }).filter(p => !formData.productRules.includes(p._id));

  // Filter configs based on search
  const filteredConfigs = configs.filter(config =>
    config.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    config.ncpdpId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    config.stateRules.some(s => s.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Count active configs
  const activeCount = configs.filter(c => c.isActive).length;
  const defaultConfig = configs.find(c => c.isDefault);

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pharmacy Configuration</h1>
          <p className="text-gray-600 mt-1">Manage pharmacy routing rules for order fulfillment</p>
        </div>
        <button
          onClick={handleOpenCreateModal}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Pharmacy
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex items-center">
            <Building2 className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Pharmacies</p>
              <p className="text-2xl font-bold text-gray-900">{configs.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Active</p>
              <p className="text-2xl font-bold text-gray-900">{activeCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex items-center">
            <Star className="h-8 w-8 text-yellow-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Default Pharmacy</p>
              <p className="text-lg font-bold text-gray-900 truncate">
                {defaultConfig ? defaultConfig.name : 'None'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Test Routing Panel */}
      <div className="bg-white rounded-lg shadow">
        <button
          onClick={() => setShowTestPanel(!showTestPanel)}
          className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center">
            <TestTube className="h-5 w-5 text-purple-600 mr-3" />
            <span className="font-medium text-gray-900">Test Routing</span>
            <span className="ml-2 text-sm text-gray-500">Preview which pharmacy will handle an order</span>
          </div>
          {showTestPanel ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </button>

        {showTestPanel && (
          <div className="px-6 pb-6 border-t border-gray-200 pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Patient State
                </label>
                <select
                  value={testState}
                  onChange={(e) => setTestState(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select a state...</option>
                  {US_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product (Optional)
                </label>
                <select
                  value={testProductId}
                  onChange={(e) => setTestProductId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select a product...</option>
                  {products.map(product => (
                    <option key={product._id} value={product._id}>
                      {product.displayName || product.drugName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={handleTestRouting}
                  disabled={testLoading}
                  className="w-full px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                >
                  {testLoading ? 'Testing...' : 'Test Routing'}
                </button>
              </div>
            </div>

            {testResult && (
              <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <h4 className="font-medium text-purple-900 mb-2">Routing Result</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-purple-600">Resolved Pharmacy:</span>
                    <span className="ml-2 font-medium text-purple-900">{testResult.resolvedNcpdpId}</span>
                  </div>
                  <div>
                    <span className="text-purple-600">Matched By:</span>
                    <span className={`ml-2 font-medium ${
                      testResult.matchedBy === 'state' ? 'text-green-700' :
                      testResult.matchedBy === 'product' ? 'text-blue-700' :
                      testResult.matchedBy === 'default' ? 'text-yellow-700' :
                      'text-gray-700'
                    }`}>
                      {testResult.matchedBy.charAt(0).toUpperCase() + testResult.matchedBy.slice(1)} Rule
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search pharmacies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Pharmacy List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading pharmacies...</p>
          </div>
        ) : filteredConfigs.length === 0 ? (
          <div className="p-8 text-center">
            <Building2 className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No pharmacies found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {configs.length === 0
                ? 'Get started by adding your first pharmacy configuration.'
                : 'Try adjusting your search criteria.'}
            </p>
          </div>
        ) : (
          <div className="data-table-container">
            <div className="data-table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Pharmacy</th>
                    <th>NCPDP ID</th>
                    <th>State Rules</th>
                    <th>Product Rules</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredConfigs.map((config) => (
                    <tr key={config._id}>
                      <td className="cell-primary">
                        <div className="cell-inline cell-inline-icon">
                          {config.name}
                          {config.isDefault && (
                            <span title="Default Pharmacy">
                              <Star className="h-4 w-4 text-yellow-500" />
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="cell-mono">{config.ncpdpId}</td>
                      <td className="cell-secondary">
                        <div className="flex flex-wrap gap-1">
                          {config.stateRules.length > 0 ? (
                            config.stateRules.slice(0, 5).map(state => (
                              <span
                                key={state}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                              >
                                <MapPin className="w-3 h-3 mr-1" />
                                {state}
                              </span>
                            ))
                          ) : (
                            <span className="cell-muted">No state rules</span>
                          )}
                          {config.stateRules.length > 5 && (
                            <span className="text-xs text-gray-500">
                              +{config.stateRules.length - 5} more
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="cell-secondary">
                        <div className="flex flex-wrap gap-1">
                          {config.productRules.length > 0 ? (
                            config.productRules.slice(0, 3).map(product => (
                              <span
                                key={product._id}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800"
                              >
                                <Package className="w-3 h-3 mr-1" />
                                {product.displayName || product.drugName || 'Unknown'}
                              </span>
                            ))
                          ) : (
                            <span className="cell-muted">No product rules</span>
                          )}
                          {config.productRules.length > 3 && (
                            <span className="text-xs text-gray-500">
                              +{config.productRules.length - 3} more
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="cell-secondary">
                        {config.isActive ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            <XCircle className="w-3 h-3 mr-1" />
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="cell-actions">
                        <div className="table-actions">
                          <button
                            onClick={() => handleOpenEditModal(config)}
                            className="table-action-btn table-action-btn-primary"
                            title="Edit"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                          {deleteConfirmId === config._id ? (
                            <>
                              <button
                                onClick={() => handleDelete(config._id)}
                                className="table-action-btn table-action-btn-danger"
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => setDeleteConfirmId(null)}
                                className="table-action-btn table-action-btn-secondary"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => setDeleteConfirmId(config._id)}
                              className="table-action-btn table-action-btn-danger"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingConfig ? 'Edit Pharmacy' : 'Add New Pharmacy'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pharmacy Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., East Coast Pharmacy"
                />
              </div>

              {/* NCPDP ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  NCPDP ID *
                </label>
                <input
                  type="text"
                  value={formData.ncpdpId}
                  onChange={(e) => setFormData(prev => ({ ...prev, ncpdpId: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                  placeholder="e.g., 1234567"
                />
                <p className="mt-1 text-xs text-gray-500">
                  The unique identifier for this pharmacy in the 5axis system
                </p>
              </div>

              {/* State Rules */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State Rules
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Orders from patients in these states will be routed to this pharmacy
                </p>

                {/* Selected States */}
                {formData.stateRules.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.stateRules.map(state => (
                      <span
                        key={state}
                        className="inline-flex items-center px-2 py-1 rounded-md text-sm font-medium bg-blue-100 text-blue-800"
                      >
                        {state}
                        <button
                          type="button"
                          onClick={() => handleRemoveState(state)}
                          className="ml-1 text-blue-600 hover:text-blue-800"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* State Selector */}
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      handleAddState(e.target.value);
                      e.target.value = '';
                    }
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Add a state...</option>
                  {US_STATES.filter(s => !formData.stateRules.includes(s)).map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              {/* Product Rules */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Rules
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Orders containing these products will be routed to this pharmacy (state rules take priority)
                </p>

                {/* Selected Products */}
                {formData.productRules.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.productRules.map(productId => {
                      const product = getProductById(productId);
                      return (
                        <span
                          key={productId}
                          className="inline-flex items-center px-2 py-1 rounded-md text-sm font-medium bg-purple-100 text-purple-800"
                        >
                          <Package className="w-3 h-3 mr-1" />
                          {product?.displayName || product?.drugName || 'Unknown Product'}
                          <button
                            type="button"
                            onClick={() => handleRemoveProduct(productId)}
                            className="ml-1 text-purple-600 hover:text-purple-800"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Product Search and Selector */}
                <div className="space-y-2">
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    placeholder="Search products..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  {productSearch && filteredProducts.length > 0 && (
                    <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                      {filteredProducts.slice(0, 10).map(product => (
                        <button
                          key={product._id}
                          type="button"
                          onClick={() => handleAddProduct(product._id)}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-purple-50 border-b border-gray-100 last:border-b-0"
                        >
                          <span className="font-medium">{product.displayName || product.drugName}</span>
                          {product.concentration && (
                            <span className="text-gray-500 ml-1">- {product.concentration}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  {!productSearch && (
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          handleAddProduct(e.target.value);
                          e.target.value = '';
                        }
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">Select a product to add...</option>
                      {products.filter(p => !formData.productRules.includes(p._id)).slice(0, 50).map(product => (
                        <option key={product._id} value={product._id}>
                          {product.displayName || product.drugName}
                          {product.concentration ? ` - ${product.concentration}` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              {/* Is Default */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData(prev => ({ ...prev, isDefault: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-900">
                  Set as default pharmacy
                </label>
              </div>
              <p className="text-xs text-gray-500 -mt-2 ml-6">
                The default pharmacy handles orders that don't match any state or product rules
              </p>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || !formData.name.trim() || !formData.ncpdpId.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Saving...' : editingConfig ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PharmacyConfig;
